import React, { createContext, useContext, useState, useEffect } from 'react';
import { config } from '../config/env';
import logger from '../utils/logger';
import { loadingBarStart } from '../services/loadingBar';
import { redirectToLogin } from '../api/auth-handler';

const CommonAuthContext = createContext(null);

export const useCommonAuth = () => {
  const context = useContext(CommonAuthContext);
  if (!context) {
    throw new Error('useCommonAuth must be used within CommonAuthProvider');
  }
  return context;
};

export const CommonAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const validateAuth = async (retryCount = 0) => {
      try {
        // Keep this delay: a 401 from /auth/validate redirects to login
        // with NO retry (the retry below only covers thrown network
        // errors), so validating before the auth cookies have
        // propagated would bounce a freshly-logged-in user back to
        // login. Shell-first rendering means this no longer blocks
        // paint — it only defers the background validation.
        if (retryCount === 0) {
          logger.debug('🔐 Waiting for cookies to be ready...');
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        logger.debug('🔐 Validating authentication with Athena backend...');
        logger.debug('🔐 Current URL:', window.location.href);
        logger.debug('🔐 Calling validation endpoint: /api/v1/auth/validate');

        // Call Athena backend to validate common auth JWT
        const response = await fetch('/api/v1/auth/validate', {
          method: 'POST',
          credentials: 'include'  // Include cookies
        });

        logger.debug('🔐 Response status:', response.status, response.statusText);

        // Handle 401 specifically - session terminated
        if (response.status === 401) {
          logger.warn('🔐 401 Unauthorized - session has been terminated');

          // Try to get error details
          try {
            const errorData = await response.json();
            logger.debug('🔐 401 Error details:', errorData);
            if (errorData.detail) {
              logger.debug('🔐 Session termination reason:', errorData.detail);
            }
          } catch (e) {
            // Response might not be JSON
          }

          // Clear local state and redirect
          setLoading(false);
          setIsAuthenticated(false);
          localStorage.removeItem('athena_session');
          localStorage.removeItem('user_data');
          // Session terminated (validate 401) → centralized redirect (product=athena + return path).
          redirectToLogin();
          return;
        }

        // Check if response is OK and has content
        if (!response.ok) {
          const errorText = await response.text();
          logger.error('🔐 Validation failed with status:', response.status);
          logger.error('🔐 Error response:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
        }

        // Check if response has content before parsing JSON
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0') {
          logger.error('🔐 Empty response from server');
          throw new Error('Empty response from server');
        }

        const data = await response.json();
        logger.debug('🔐 Auth validation response:', data);
        logger.debug('🔐 User products:', data.products);

        if (data.valid) {
          // Server is authoritative: /auth/validate cross-checks the live
          // Common-Auth session, so a logged-out session already fails above.
          // (No client-side last_logout timestamp compare — it was redundant
          // and never expired, lingering in localStorage forever.)
          setUser({
            user_id: data.user_id,
            tenant_id: data.tenant_id,
            email: data.email,
            company_name: data.company_name,
            products: data.products,
            session_id: data.session_id
          });
          setIsAuthenticated(true);
          logger.debug('✅ Authentication valid for user:', data.user_id);
        } else {
          logger.warn('❌ Authentication invalid:', data.error);
          // Session invalid → centralized redirect (product=athena + return path).
          redirectToLogin();
          return;
        }

        // Success - stop loading
        setLoading(false);
      } catch (error) {
        logger.error('❌ Auth validation failed:', error);

        // If this might be a timing issue and we haven't retried yet, retry once
        if (retryCount < 1) {
          logger.debug('🔄 Retrying validation in case of cookie propagation delay...');
          await new Promise(resolve => setTimeout(resolve, 500));
          return validateAuth(retryCount + 1);
        }

        // Validation failed → centralized redirect (product=athena + return path).
        setLoading(false);
        redirectToLogin();
        return;
      }
    };

    // Run the validation behind the top progress bar (shell-first: the
    // app chrome renders immediately; this is the only loading signal).
    const doneLoadingBar = loadingBarStart();
    validateAuth().finally(doneLoadingBar);
  }, []);

  const logout = async () => {
    // Aggressively drop all app-visible auth state up front, regardless of how
    // the network call resolves. No logout flags — the server (which clears the
    // cookies + kills the session) is authoritative; the bfcache guard handles
    // a Back-button restore. sessionStorage.clear() is safe now nothing stores
    // logout state there.
    const clearLocalState = () => {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('athena_session');
      localStorage.removeItem('user_data');
      sessionStorage.clear();
    };

    // replace() (not href) so the logged-in page is not retained as the
    // back-entry in this navigation path.
    const toLogin = () => window.location.replace(`${config.COMMON_AUTH_LOGIN_URL}?logout=true`);

    try {
      logger.debug('🚪 Logging out - invalidating all sessions via Common Auth');
      clearLocalState();

      // Step 1: Fetch CSRF token from Common Auth
      const csrfResponse = await fetch(`${config.COMMON_AUTH_URL}/api/v1/auth/csrf`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!csrfResponse.ok) {
        throw new Error(`CSRF fetch failed: ${csrfResponse.status}`);
      }

      const { csrf_token } = await csrfResponse.json();

      // Step 2: POST logout to Common Auth — deletes the Redis session, clears
      // all auth cookies server-side, and cascades invalidation to products.
      const logoutResponse = await fetch(`${config.COMMON_AUTH_URL}/api/v1/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf_token
        }
      });

      if (!logoutResponse.ok) {
        logger.warn('⚠️ Common Auth logout returned status:', logoutResponse.status);
      }

      logger.debug('✅ Logout complete - redirecting to login');
      toLogin();
    } catch (error) {
      logger.error('❌ Logout error:', error);
      // Even if the network call failed, make sure the user is logged out locally.
      clearLocalState();
      toLogin();
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    logout
  };

  return (
    <CommonAuthContext.Provider value={value}>
      {children}
    </CommonAuthContext.Provider>
  );
};

export default CommonAuthContext;
